import React, { FC, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getData,
  getAssetsComposition,
  getHasNextPage,
  getLoading,
  getScrollHash,
  getSelected,
} from '../selectors';
import InfiniteLoader from 'react-window-infinite-loader';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import * as finderActions from '../actions';
import { isLikeCtrlKey, Loading } from '../model';
import {
  AssetRowFillSkeleton,
  AssetRowSkeleton,
} from '../../skeleton/components/AssetGridSkeleton';
import { Asset } from './Asset';
import { throttle } from 'lodash';
import { Asset as AssetType } from '../../../api';

type RowData = {
  rows: AssetType[][];
  assetsPerRow: number;
  hasNextPage: boolean;
};

const Row = memo(({ index, style, data }: ListChildComponentProps<RowData>) => {
  const { rows, assetsPerRow, hasNextPage } = data;
  const isLoaded = !hasNextPage || index < rows.length;

  // Ещё не подгруженная страница: вместо спиннера — приглушённые карточки-призраки
  // ровно там, где появится контент, с негромкой подписью поверх них.
  if (!isLoaded) {
    return (
      <div style={style}>
        <AssetRowSkeleton perRow={assetsPerRow} muted label="Loading more files…" />
      </div>
    );
  }

  const row = rows[index] || [];
  const missing = assetsPerRow - row.length;
  // Страница с бэка почти никогда не кратна числу колонок, поэтому последний ряд
  // обрывается на середине. Пока впереди есть ещё страница, добиваем его призраками:
  // иначе рядом с полным рядом-заглушкой снизу оставалась дыра на пару миниатюр.
  const fillWithSkeleton = hasNextPage && index === rows.length - 1 && missing > 0;

  return (
    <div style={style} className="al-finder-assets__row">
      {row.map((asset) => (
        <Asset id={asset.id} key={asset.id} />
      ))}

      {fillWithSkeleton ? (
        <AssetRowFillSkeleton count={missing} startIndex={row.length} />
      ) : (
        Array(missing)
          .fill(null)
          .map((_, i) => <div key={i} />)
      )}
    </div>
  );
});

export const AssetList: FC<{ width: number; height: number }> = ({ width, height }) => {
  const dispatch = useDispatch();
  const { assets } = useSelector(getData);
  const selected = useSelector(getSelected);
  const loading = useSelector(getLoading);
  const hasNextPage = useSelector(getHasNextPage);
  const scrollHash = useSelector(getScrollHash);

  const assetsComposition = useSelector(getAssetsComposition);
  const { containerWidth, assetHeight, gapVertical, rows, assetsPerRow } = assetsComposition;

  const infiniteLoaderRef = useRef<InfiniteLoader & { _listRef?: FixedSizeList }>(null);
  useEffect(() => {
    infiniteLoaderRef.current?.resetloadMoreItemsCache();
  }, [assets]);

  useEffect(() => {
    // https://github.com/bvaughn/react-window/issues/463#issuecomment-641486656
    infiniteLoaderRef.current?._listRef?.scrollTo(0);
  }, [scrollHash]);

  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        if (document.activeElement instanceof HTMLTextAreaElement) {
          return;
        }
        if (
          document.activeElement instanceof HTMLInputElement &&
          document.activeElement.type === 'text'
        ) {
          return;
        }
        dispatch(
          finderActions.navigationKeyPressed({
            key: e.key,
            shiftKey: e.shiftKey,
            ctrlKey: isLikeCtrlKey(e),
          }),
        );
      }
    };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  });

  // Scroll to selected item when selection or layout changes
  const selectedIndex = rows.findIndex((row) => row.find(({ id }) => id === selected.lastId));
  useEffect(() => {
    if (selectedIndex !== -1) {
      setTimeout(() => infiniteLoaderRef.current?._listRef?.scrollToItem(selectedIndex), 0);
    }
  }, [selectedIndex, assetsPerRow]);

  const saveContainerWidth = useMemo(
    () =>
      throttle(
        (newWidth: number) => dispatch(finderActions.containerWidthChanged({ width: newWidth })),
        300,
      ),
    [dispatch],
  );
  useEffect(() => {
    return () => {
      saveContainerWidth.cancel();
    };
  }, [saveContainerWidth]);

  if (width !== containerWidth) {
    saveContainerWidth(width);
  }

  const itemCount = hasNextPage ? rows.length + 1 : rows.length;

  const loadMoreItems = () => {
    if (loading === Loading.none && rows.length > 0) {
      dispatch(finderActions.nextPageRequested());
    }
  };

  const isItemLoaded = useCallback(
    (index: number) => !hasNextPage || index < rows.length,
    [hasNextPage, rows.length],
  );

  // Memoize itemData to prevent Row re-renders when only selection changes
  const itemData: RowData = useMemo(
    () => ({ rows, assetsPerRow, hasNextPage }),
    [rows, assetsPerRow, hasNextPage],
  );

  // Stable key extractor based on first asset ID in the row
  const itemKey = useCallback((index: number, data: RowData) => {
    const row = data.rows[index];
    return row?.[0]?.id ?? `loading-${index}`;
  }, []);

  return (
    <InfiniteLoader
      ref={infiniteLoaderRef}
      isItemLoaded={isItemLoaded}
      itemCount={itemCount}
      loadMoreItems={loadMoreItems}
    >
      {({ onItemsRendered, ref }) => (
        <FixedSizeList
          ref={ref}
          onItemsRendered={onItemsRendered}
          width="100%"
          height={height}
          itemCount={itemCount}
          itemSize={assetHeight + gapVertical}
          itemData={itemData}
          itemKey={itemKey}
          className="al-finder-assets__list"
        >
          {Row}
        </FixedSizeList>
      )}
    </InfiniteLoader>
  );
};
